
import { playSoftSuccessSound } from './audioUtils';

export const createBarcodeDetectionHandler = (
  onScanResult: (barcode: string) => void,
  onClose: () => void,
  audioContextRef: React.MutableRefObject<AudioContext | null>,
  soundEnabled: boolean,
  hasDetectedBarcodeRef: React.MutableRefObject<boolean>,
  stopScanning: () => Promise<void>,
  toast: any
) => {
  return async (barcode: string, confidence: number = 95) => {
    // Prevent multiple detections
    if (hasDetectedBarcodeRef.current) {
      console.log('⚠️ Barcode already detected, ignoring...');
      return;
    }
    
    hasDetectedBarcodeRef.current = true;
    console.log('🎯 BARCODE DETECTED:', barcode, 'with confidence:', confidence);
    
    // Play sound immediately
    await playSoftSuccessSound(audioContextRef, soundEnabled);
    
    // Show toast
    toast({
      title: "✅ Barcode Detected!",
      description: `Found: ${barcode}`,
      duration: 3000,
    });
    
    // Call the callback IMMEDIATELY to pass the scanned value
    console.log('📤 Calling onScanResult with barcode:', barcode);
    onScanResult(barcode);
    
    // Stop scanning and cleanup
    console.log('🛑 Starting cleanup after successful scan...');
    await stopScanning();
    
    // Close the scanner after a short delay
    setTimeout(() => {
      console.log('🚪 Closing scanner...');
      onClose();
    }, 500);
  };
};

export const createBarcodeListener = (
  handleBarcodeDetected: (barcode: string, confidence?: number) => Promise<void>,
  setDetectionStrength: (value: number | ((prev: number) => number)) => void,
  setError: (error: string | null) => void
) => {
  return {
    didScan: async (barcodeCapture: any, session: any) => {
      console.log('📷 Scan event triggered');
      console.log('📊 Session data:', session);
      
      // Handle the CORRECT Scandit session structure
      let barcodeData = null;
      
      // Check for _newlyRecognizedBarcode (singular object)
      if (session._newlyRecognizedBarcode) {
        const barcode = session._newlyRecognizedBarcode;
        console.log('🔍 Found _newlyRecognizedBarcode:', barcode);
        
        // Extract data from the _data property
        if (barcode._data) {
          barcodeData = barcode._data;
          console.log('✅ Got barcode data from _data:', barcodeData);
        }
      }
      // Fallback: check for newlyRecognizedBarcodes array
      else if (session.newlyRecognizedBarcodes && Array.isArray(session.newlyRecognizedBarcodes) && session.newlyRecognizedBarcodes.length > 0) {
        const firstBarcode = session.newlyRecognizedBarcodes[0];
        console.log('🔍 Found newlyRecognizedBarcodes array:', firstBarcode);
        
        // Try multiple extraction methods for array format
        if (firstBarcode._data) {
          barcodeData = firstBarcode._data;
          console.log('✅ Got barcode data from array _data:', barcodeData);
        } else if (firstBarcode.data) {
          barcodeData = firstBarcode.data;
          console.log('✅ Got barcode data from array data:', barcodeData);
        } else if (firstBarcode.value) {
          barcodeData = firstBarcode.value;
          console.log('✅ Got barcode data from array value:', barcodeData);
        } else if (firstBarcode.text) {
          barcodeData = firstBarcode.text;
          console.log('✅ Got barcode data from array text:', barcodeData);
        }
      }
      
      if (barcodeData && typeof barcodeData === 'string' && barcodeData.trim()) {
        const confidence = Math.floor(Math.random() * 20) + 80;
        setDetectionStrength(100);
        console.log('🎯 Processing valid barcode:', barcodeData.trim());
        await handleBarcodeDetected(barcodeData.trim(), confidence);
        return;
      } else {
        console.warn('⚠️ Could not extract valid barcode data from session:', session);
      }
      
      // Update detection strength for visual feedback
      setDetectionStrength(prev => Math.min(prev + 10, 70));
    },
    
    didUpdateSession: () => {
      setDetectionStrength(prev => Math.min(prev + 3, 60));
    },
    
    didFailWithError: (barcodeCapture: any, error: any) => {
      console.error('❌ Barcode capture error:', error);
      setError(`Scan error: ${error.message || error}`);
    }
  };
};
